/* eslint-disable @typescript-eslint/no-use-before-define */
import { LinkButton } from '@euler/app/components/LinkButton';
import { Typography } from '@euler/app/components/typography';
import { QuotationCatalogSubjectCell } from '@euler/app/flows/task/pages/quotation/components/QuotationCatalogSubjectCell';
import { useQuotationSelectionContext } from '@euler/app/flows/task/pages/quotation/components/QuotationSelectionContext';
import { EmptyView } from '@euler/components/EmptyView';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetHeight } from '@euler/functions/useBottomSheetModal';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { withConfirmation } from '@euler/utils';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import BottomSheet, { BottomSheetFlatList } from '@gorhom/bottom-sheet';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { FlatList, Platform, TouchableOpacity, View } from 'react-native';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BehaviorSubject, Subject } from 'rxjs';
import { useTheme } from 'styled-components';

const kHandleHeight = 24;
const kCellHeight = 44;

export type QuotationSubjectsBottomSheetAction = {
  type: 'reveal';
  index?: number;
};

export const useQuotationSubjectsBottomSheetFooterHeight = () => {
  const insets = useSafeAreaInsets();
  const { rpx } = useResponsiveContext();
  return rpx(54) + Math.max(rpx(10), insets.bottom) + kHandleHeight;
};

export const QuotationSubjectsBottomSheet = memo(
  ({
    action$,
    onDone,
  }: {
    action$: Subject<QuotationSubjectsBottomSheetAction>;
    onDone?: (selection: string[]) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { navBarHeight } = useSystemMetrics();
    const height = useGeneralBottomSheetHeight();
    const footerHeight = useQuotationSubjectsBottomSheetFooterHeight();
    const bottomSheetRef = useRef<BottomSheet>(null);
    const minSnapPoint = footerHeight;
    const lowerWatermarkSnapPoint = rpx(kCellHeight) * 3 + minSnapPoint;
    const mediumWatermarkSnapPoint = rpx(kCellHeight) * 10 + minSnapPoint;
    const maxSnapPoint = height - navBarHeight;
    const snapPoints = useMemo(
      () => [
        minSnapPoint,
        lowerWatermarkSnapPoint,
        mediumWatermarkSnapPoint,
        maxSnapPoint,
      ],
      [
        lowerWatermarkSnapPoint,
        maxSnapPoint,
        mediumWatermarkSnapPoint,
        minSnapPoint,
      ],
    );

    const { selection$, toggleQuotationSubject, clearSelection } =
      useQuotationSelectionContext();

    const [selection] = useBehaviorSubject(selection$);

    const animatedIndex = useSharedValue(0);
    const animatedPosition = useSharedValue(0);
    const translateYOffset = rpx(50);

    const animatedEmptyStyle = useAnimatedStyle(() => {
      const visibleHeight = interpolate(
        animatedPosition.value,
        [
          height - maxSnapPoint,
          height - mediumWatermarkSnapPoint,
          height - lowerWatermarkSnapPoint,
          height - minSnapPoint,
        ],
        [
          maxSnapPoint - minSnapPoint,
          mediumWatermarkSnapPoint - minSnapPoint,
          lowerWatermarkSnapPoint - minSnapPoint,
          0,
        ],
      );
      return {
        opacity: interpolate(
          animatedPosition.value,
          [
            height - maxSnapPoint,
            height - mediumWatermarkSnapPoint,
            height - lowerWatermarkSnapPoint,
            height - minSnapPoint,
          ],
          [1, 1, 1, 0],
        ),
        transform: [
          {
            translateY: visibleHeight / 2 - translateYOffset,
          },
          {
            scale: interpolate(
              animatedPosition.value,
              [
                height - maxSnapPoint,
                height - mediumWatermarkSnapPoint,
                height - lowerWatermarkSnapPoint,
                height - minSnapPoint,
              ],
              [1, 1, 1, 0],
            ),
          },
        ],
      };
    });

    const checked$ = useCreation(() => new BehaviorSubject<string[]>([]), []);
    const [checked, setChecked] = useBehaviorSubject(checked$);

    const isAllChecked =
      checked.length > 0 && checked.length === selection.length;

    const onItemPress = usePersistFn((name: string) => {
      const index = checked.indexOf(name);
      if (index >= 0) {
        setChecked(l => l.filter(x => x !== name));
      } else {
        setChecked(l => [...l, name]);
      }
    });

    const onRemoveItem = useCallback(
      (name: string) => {
        toggleQuotationSubject(name);
        setChecked(l => l.filter(x => x !== name));
      },
      [setChecked, toggleQuotationSubject],
    );

    const onSelectedButtonPress = useCallback(() => {
      bottomSheetRef.current?.snapToIndex(2);
    }, []);

    const onToggleSelectAll = usePersistFn(() => {
      if (isAllChecked) {
        checked$.next([]);
      } else {
        checked$.next(selection.slice());
      }
    });

    const onClear = usePersistFn(async () => {
      if (
        await withConfirmation({
          title: '确认',
          message: '是否确认清空全部报价项目?',
        })
      ) {
        checked$.next([]);
        clearSelection();
      }
    });

    useEffect(() => {
      const sub = action$.subscribe(action => {
        if (action.type === 'reveal') {
          bottomSheetRef.current?.snapToIndex(action.index ?? 2);
        }
      });
      return () => sub.unsubscribe();
    }, [action$]);

    // BottomSheet specific scroll view components do not work on Web right now.
    // see https://github.com/gorhom/react-native-bottom-sheet/issues/11.
    const ListComponent =
      Platform.OS === 'web' ? FlatList : BottomSheetFlatList;

    const onDonePress = useCallback(() => {
      onDone?.(selection);
    }, [onDone, selection]);

    return (
      <>
        <BottomSheet
          ref={bottomSheetRef}
          snapPoints={snapPoints}
          animatedIndex={animatedIndex}
          animatedPosition={animatedPosition}
          index={0}
          css={`
            background-color: #fff;
            box-shadow: 0 -1px ${rpx(15)}px rgba(0, 0, 0, 0.5);
            elevation: 3;
          `}
          style={{
            elevation: 3,
          }}
        >
          <ListComponent
            data={selection}
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: footerHeight,
            }}
            ListEmptyComponent={
              <Animated.View
                css={`
                  height: ${rpx(100)}px;
                `}
                style={animatedEmptyStyle}
              >
                <EmptyView message="尚未添加相关报价项目" />
              </Animated.View>
            }
            ListHeaderComponent={
              selection.length > 0 ? (
                <HeaderComponent
                  isAllChecked={isAllChecked}
                  onToggleSelectAll={onToggleSelectAll}
                  onClear={onClear}
                />
              ) : undefined
            }
            renderItem={({ item, index }) => (
              <Cell
                name={item}
                selected={selection.includes(item)}
                checked={checked.includes(item)}
                onPress={onItemPress}
                onRemove={onRemoveItem}
                first={index === 0}
                last={index === selection.length - 1}
              />
            )}
          />
        </BottomSheet>
        <BottomBar
          count={selection.length}
          checkedCount={checked.length}
          onSelectedButtonPress={onSelectedButtonPress}
          onDoneButtonPress={onDonePress}
        />
      </>
    );
  },
);

const HeaderComponent = memo(
  ({
    isAllChecked,
    onToggleSelectAll,
    onClear,
  }: {
    isAllChecked: boolean;
    onToggleSelectAll: () => void;
    onClear: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <HStack
        justifyContent="flex-start"
        css={`
          padding: 0 ${rpx(17.5)}px;
          margin-bottom: ${rpx(15)}px;
        `}
      >
        <LinkButton
          text={isAllChecked ? '取消全选' : '全选'}
          onPress={onToggleSelectAll}
        />
        <LinkButton
          text={'清空'}
          onPress={onClear}
          css={`
            margin-left: ${rpx(8)}px;
          `}
        />
      </HStack>
    );
  },
);

const BottomBar = memo(
  ({
    count,
    onSelectedButtonPress,
    onDoneButtonPress,
  }: {
    count: number;
    checkedCount: number;
    onSelectedButtonPress?: () => void;
    onDoneButtonPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const theme = useTheme();

    const paddingTop = rpx(10);
    const paddingBottom = Math.max(rpx(10), insets.bottom);
    const minHeight = rpx(44) + paddingTop + paddingBottom;

    return (
      <View
        css={`
          padding-top: ${paddingTop}px;
          padding-bottom: ${paddingBottom}px;
          min-height: ${minHeight}px;
          background-color: #fff;
          elevation: 1;
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
        `}
      >
        <HStack
          css={`
            padding: 0 ${rpx(15)}px;
            flex: 1;
          `}
        >
          <TouchableOpacity
            onPress={onSelectedButtonPress}
            css={`
              background-color: #ccc;
              height: ${rpx(44)}px;
              border-radius: ${rpx(22)}px;
              padding: 0 ${rpx(22)}px;
              align-items: center;
              justify-content: center;
              flex-direction: row;
            `}
          >
            <Icon name="clipboard" size={rpx(16)} color={'#333'} />
            <Typography.Label color={'#333'} size={rpx(16)}>
              {`已选 (${count})`}
            </Typography.Label>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={onDoneButtonPress}
            css={`
              flex: 1;
              margin-left: ${rpx(10)}px;
              height: ${rpx(44)}px;
              border-radius: ${rpx(22)}px;
              align-items: center;
              justify-content: center;
              background-color: ${theme.link};
            `}
          >
            <Typography.Label size={rpx(20)} color="#fff" regular>
              完成
            </Typography.Label>
          </TouchableOpacity>
        </HStack>
      </View>
    );
  },
);

const Cell = memo(
  ({
    name,
    selected,
    checked,
    onPress,
    onRemove,
  }: {
    first?: boolean;
    last?: boolean;
    name: string;
    checked?: boolean;
    selected: boolean;
    onPress: (name: string) => void;
    onRemove: (name: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      construction: {
        schedule: {
          workItem: {
            cell: { textLight, bgLight },
          },
        },
      },
    } = useTheme();

    const onCellPress = useCallback(() => {
      onPress?.(name);
    }, [name, onPress]);

    const handleRemove = useCallback(() => {
      onRemove?.(name);
    }, [name, onRemove]);

    return (
      <QuotationCatalogSubjectCell
        title={name}
        selected={selected}
        checked={checked}
        checkable={true}
        onPress={onCellPress}
        onRemove={handleRemove}
        titleStyle={{
          color: textLight,
          fontFamily: FontFamily.NotoSans.Light,
          fontSize: rpx(16),
        }}
        css={`
          background-color: ${bgLight};
          padding-top: ${rpx(5)}px;
          padding-bottom: ${rpx(5)}px;
          padding-left: ${rpx(15)}px;
          padding-right: ${rpx(8)}px;
          margin: 0;
          border-bottom-width: 1px;
          border-bottom-color: #eee;
          border-radius: 0;
        `}
      />
    );
  },
);
